import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { OpenInNew as OpenInNewIcon } from 'components/icons';
import InfoTooltip from 'components/InfoTooltip';
import { Link as RouterLink } from 'react-router-dom';
import { useGetCurrentTenantQuery } from 'store/api/tenants';
import { t } from 'utils/getTranslations';
import {
    getInvoiceScheduleKind,
    INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID,
    ordinal,
} from 'utils/invoiceAutoGenerationSchedule';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    invoicingIntervalCode: string | null | undefined;
}

/**
 * Read-only value of the tenant-wide invoice autogeneration schedule matching the given invoicing
 * interval (interval date for date-based intervals, interval weekday for week-based ones), with a
 * link opening the Configuration page (where the setting is edited) in a new tab. Users who cannot
 * open that page (no READ.BACK_OFFICE_SETTINGS — the route's guard) get plain text instead.
 */
export const InvoiceAutoGenerationScheduleValue = ({ invoicingIntervalCode }: Props): JSX.Element | null => {
    const scheduleKind = getInvoiceScheduleKind(invoicingIntervalCode);
    const { data: tenant } = useGetCurrentTenantQuery(undefined, { skip: scheduleKind === null });
    const canOpenConfigurationPage = useHasPermissions(['READ.BACK_OFFICE_SETTINGS']);

    if (scheduleKind === null) {
        return null;
    }

    const value =
        tenant == null || invoicingIntervalCode == null
            ? '-'
            : scheduleKind === 'date'
              ? `${ordinal(tenant.invoiceAutoGenerationDaysOfMonth[invoicingIntervalCode] ?? 1)} ${t('financial-conditions.interval-date-caption')}`
              : t(
                    `financial-conditions.weekday.${tenant.invoiceAutoGenerationDaysOfWeek[invoicingIntervalCode] ?? 'MONDAY'}`
                );

    return (
        <span className="inline-flex items-center gap-1.5">
            <span className="text-body1">{value}</span>
            <InfoTooltip text="financial-conditions.tooltips.interval-schedule-tenant-wide" />
            {canOpenConfigurationPage ? (
                <RouterLink
                    to={`/settings/configuration#${INVOICE_AUTO_GENERATION_SCHEDULE_SECTION_ID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body2 text-fg hover:text-link inline-flex w-fit items-center gap-0.5 no-underline hover:underline"
                >
                    {t('financial-conditions.interval-schedule-configure-link')}
                    <OpenInNewIcon fontSize="small" />
                </RouterLink>
            ) : (
                <span className="text-body2 text-fg-secondary">
                    {t('financial-conditions.interval-schedule-configure-link')}
                </span>
            )}
        </span>
    );
};

/**
 * The same read-only schedule value presented as a labelled form field (used in the financial
 * condition modal next to the invoicing interval selector).
 */
export const InvoiceAutoGenerationScheduleReadonlyField = ({ invoicingIntervalCode }: Props): JSX.Element | null => {
    const scheduleKind = getInvoiceScheduleKind(invoicingIntervalCode);

    if (scheduleKind === null) {
        return null;
    }

    return (
        <InputWrapper
            name="invoiceAutoGenerationSchedule"
            label={
                scheduleKind === 'date' ? 'financial-conditions.interval-date' : 'financial-conditions.interval-weekday'
            }
            hasErrors={false}
            isEmpty={false}
            readonly
        >
            <InvoiceAutoGenerationScheduleValue invoicingIntervalCode={invoicingIntervalCode} />
        </InputWrapper>
    );
};
